import React, { useEffect, useState, useRef } from 'react';
import {
  FaPlaneArrival,
  FaHeadset,
  FaArrowDown,
  FaCalendarDay,
} from 'react-icons/fa';
import * as turf from '@turf/turf';

const TRAFFIC_URL = 'https://data.vatsim.net/v3/vatsim-data.json';
const airports = [
  { code: 'EFRO', coords: [25.8276, 66.5608], fmisid: 137190 },
  { code: 'ENTC', coords: [18.9144, 69.681], fmisid: 0 },
  { code: 'ESNQ', coords: [20.3368, 67.8219], fmisid: 0 },
  { code: 'EFHK', coords: [24.954, 60.3167], fmisid: 100968 },
  { code: 'EDDF', coords: [8.5556, 50.0377], fmisid: 0 },
];

const translations = {
  en: {
    title: 'Arrivals',
    time: 'Time',
    origin: 'Origin',
    airline: 'Airline',
    flight: 'Flight',
    aircraft: '',
    status: 'Status',
  },
  fi: {
    title: 'Saapuvat',
    time: 'Aika',
    origin: 'Lähtöpaikka',
    airline: 'Lentoyhtiö',
    flight: 'Lento',
    aircraft: '',
    status: 'Lennon tila',
  },
  sv: {
    title: 'Ankomster',
    time: 'Tid',
    origin: 'Från',
    airline: 'Flygbolag',
    flight: 'Flyg',
    aircraft: '',
    status: 'Status',
  },
};

const statusTranslations = {
  en: {
    enroute: 'Enroute',
    arrived: 'Landed',
    landing: 'Landing',
    approaching: 'Approaching',
    departing: 'Departing',
    delivering: 'Delivering',
  },
  fi: {
    enroute: 'Matkalla',
    arrived: 'Laskeutunut',
    landing: 'Laskeutuu',
    approaching: 'Lähestyy',
    departing: 'Lähdössä',
    delivering: 'Toimittaa',
  },
  sv: {
    enroute: 'På väg',
    arrived: 'Anlänt',
    landing: 'Landar',
    approaching: 'Inflygning',
    departing: 'Avgår',
    delivering: 'Levererar',
  },
};

const CYCLE_MS = 2000;
const HALF_MS = CYCLE_MS / 2;

const FlightBoard = () => {
  const [airlineCache, setAirlineCache] = useState({});
  const [airportCache, setAirportCache] = useState({});
  const [weather, setWeather] = useState([]);
  const [metar, setMetar] = useState('');
  const [trafficData, setTrafficData] = useState([]);

  const search =
    window.location.search || window.location.hash.split('?')[1] || '';
  const queryParams = new URLSearchParams(search);

  const eventTitle = queryParams.get('title') || 'Fly and See';
  const eventTitleEnd = queryParams.get('title_red') || 'Santa';
  const activeIcao = queryParams.get('icao')?.toUpperCase() || 'EFRO';

  const activeAirport =
    airports.find((a) => a.code === activeIcao) || airports[0];

  const animOffsetRef = useRef(Date.now() % CYCLE_MS);

  const urlStart = 'https://gid-backend.lusep.fi'; // https://gid-backend.lusep.fi http://localhost:5000

  const [langIndex, setLangIndex] = useState(0);
  const [utcTime, setUtcTime] = useState('');
  const langs = ['en', 'fi', 'sv'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(TRAFFIC_URL);
        const data = await res.json();
        const filtered = data.pilots.filter(
          (p) => p.flight_plan && p.flight_plan.arrival === activeAirport.code,
        );
        setTrafficData(filtered);
      } catch (err) {
        console.error('Error fetching traffic data:', err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  // Rotate languages
  useEffect(() => {
    const interval = setInterval(() => {
      setLangIndex((prev) => (prev + 1) % langs.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // load weather every 5 minutes
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://opendata.fmi.fi/timeseries?fmisid=${activeAirport.fmisid}&param=smartsymbol,temperature,winddirection,WindSpeedMS&lang=en`,
        );
        const data = await response.text();
        const lines = data.trim().split('\n');

        // First line = header, second line = latest values
        const headers = lines[0].trim().split(/\s+/);
        const values = lines[1].trim().split(/\s+/);

        const parsed = {
          smartsymbol: values[0],
          temperature: Number(values[1]),
          windDirection: Number(values[2]),
          windSpeed: (Number(values[3]) * 1.94384).toFixed(0) + ' kts',
        };

        setWeather(parsed);
      } catch (error) {
        console.error('Error fetching weather data:', error);
      }
    };

    fetchWeather(); // run immediately once

    const interval = setInterval(fetchWeather, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, [activeAirport.fmisid]);

  // Update UTC time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hh = String(now.getUTCHours()).padStart(2, '0');
      const mm = String(now.getUTCMinutes()).padStart(2, '0');
      setUtcTime(`${hh}:${mm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchMetar = async () => {
      try {
        const response = await fetch(
          `https://api.met.no/weatherapi/tafmetar/1.0/metar.txt?icao=${activeAirport.code}`,
          {
            headers: {
              'User-Agent': 'MyReactApp/1.0 (myemail@example.com)', // required by api.met.no
            },
          },
        );
        const text = await response.text();

        // Split into lines, filter out empty ones
        const lines = text.trim().split('\n').filter(Boolean);

        // Take the last one
        const lastLine = lines[lines.length - 1];

        setMetar(lastLine);
      } catch (error) {
        console.error('Failed to fetch METAR:', error);
      }
    };

    // Fetch immediately
    fetchMetar();

    // Then fetch every 4 minutes
    const interval = setInterval(fetchMetar, 3 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const t = translations[langs[langIndex]];

  // Fetch airline info and cache it
  const fetchAirline = async (icao) => {
    if (!icao) return null;
    if (airlineCache[icao]) return airlineCache[icao];
    try {
      const searchIcao = icao === 'NOZ' ? 'NSZ' : icao;
      const res = await fetch(
        `${urlStart}/api/airlines/search/icao?q=${searchIcao}`,
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const airline = data[0];

        const logo = `${urlStart}/api/airline-logo/${airline.iata.replace(/\*/g, '')}`;
        const obj = { ...airline, logo };
        setAirlineCache((prev) => ({ ...prev, [icao]: obj }));
        return obj;
      }
    } catch (e) {
      console.error('Airline fetch failed', e);
    }
    return null;
  };

  const fetchAirport = async (icao) => {
    if (!icao) return null;
    if (airportCache[icao]) return airportCache[icao];

    try {
      const res = await fetch(`${urlStart}/api/airports/search/icao?q=${icao}`);
      const data = await res.json();

      if (data && data.length > 0) {
        const municipality = data[0].municipality || icao;
        setAirportCache((prev) => ({ ...prev, [icao]: municipality }));
        return municipality;
      }
      return icao;
    } catch (error) {
      console.error('Error fetching airport:', error);
      return icao;
    }
  };

  const calculateArrivalTime = (deptime, enroute_time) => {
    if (!deptime || !enroute_time) return null;

    const totalMinutes =
      parseInt(deptime.slice(0, 2)) * 60 +
      parseInt(deptime.slice(2, 4)) +
      parseInt(enroute_time.slice(0, 2)) * 60 +
      parseInt(enroute_time.slice(2, 4));

    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    return (
      `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}` + (totalMinutes >= 1440 ? '' : '')
    );
  };

  const [arrivals, setArrivals] = useState([]);

  useEffect(() => {
    const processFlights = async () => {
      const flights = await Promise.all(
        trafficData
          .filter((f) => f.flight_plan?.arrival === activeAirport.code)
          .map(async (f) => {
            const from = turf.point([f.longitude, f.latitude]);
            const to = turf.point(activeAirport.coords);
            const distance = turf.distance(from, to, {
              units: 'nauticalmiles',
            });

            console.log(f.callsign + ' ' + f.altitude);
            // XNO1013 altitude 355

            let status = 'enroute';
            if (f.altitude < 800 && distance < 1.3) {
              status = 'arrived';
            } else if (
              f.altitude < 2400 &&
              distance < 5 &&
              f.groundspeed > 20
            ) {
              status = 'landing';
            } else if (distance < 40 && f.groundspeed > 20) {
              status = 'approaching';
            } else if (f.altitude < 5000 && f.groundspeed < 50) {
              status = 'departing';
            }

            const match = f.callsign?.match(/^([A-Z]{3})\d.+/i);
            const airlineIcao = match ? match[1].toUpperCase() : '';
            let airline = await fetchAirline(airlineIcao);
            const origin =
              (await fetchAirport(f.flight_plan?.departure)) ||
              f.flight_plan?.departure ||
              'Unknown';
            const arrivalTime =
              calculateArrivalTime(
                f.flight_plan?.deptime,
                f.flight_plan?.enroute_time,
              ) || 'NIL';

            return {
              ...f,
              distance,
              status,
              airlineIcao,
              airline,
              origin,
              arrivalTime,
            };
          }),
      );

      flights.sort((a, b) => {
        const statusPriority = {
          arrived: 0,
          landing: 1,
          approaching: 2,
          enroute: 3,
          departing: 4,
        };

        const aStatusPriority = statusPriority[a.status] ?? 99;
        const bStatusPriority = statusPriority[b.status] ?? 99;

        // First sort by status priority
        if (aStatusPriority !== bStatusPriority) {
          return aStatusPriority - bStatusPriority;
        }

        // If both are approaching or landing, sort by altitude
        if (a.status === 'approaching' && b.status === 'approaching') {
          return a.altitude - b.altitude;
        }
        if (a.status === 'landing' && b.status === 'landing') {
          return a.altitude - b.altitude;
        }

        // For "Enroute" and "Departing", sort by distance ascending
        return a.distance - b.distance;
      });

      setArrivals(flights);
    };

    processFlights();
  }, [trafficData]);

  return (
    <div className={'h-[100vh] overflow-hidden select-none text-white'}>
      <div
        className={
          'max-w-[600px] bg-gradient-to-br from-slate-950 via-slate-800 to-slate-950 bg-[length:300%_300%] animate-gradient'
        }
      >
        <div className={'flex justify-between items-end'}>
          <h1 className="flex gap-2 items-center text-4xl py-2 pb-0 pl-3 font-extrabold">
            <FaCalendarDay className={'text-2xl w-8'} />
            <span className={''}>{eventTitle}</span>{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-pink-600">
              {eventTitleEnd}
            </span>
          </h1>
          <img src={'img/logo.svg'} alt={'Logo'} className={'w-[90px] mr-2'} />
        </div>

        <div>
          <div className="text-xl pl-3 pt-2 font-medium flex items-center gap-3">
            <img
              src={`/img/fmi/${weather.smartsymbol}.svg`}
              alt="Weather"
              className="w-8 h-8"
            />
            <div className={'flex flex-col gap-0 leading-none'}>
              <span className={'text-xs max-w-[600px] mb-1 text-gray-300'}>
                {metar || 'Loading...'}
              </span>
              <div className={'font-bold text-white flex gap-1 items-center'}>
                <FaArrowDown
                  alt="Wind"
                  className=""
                  style={{
                    transform: `rotate(${weather.windDirection}deg)`,
                  }}
                />
                {weather.windSpeed}&ensp;{weather.temperature}°C
              </div>
            </div>
          </div>
        </div>

        <div className={'pb-2'}>
          <div className="text-xl pl-3 pt-2 font-medium flex items-center gap-3">
            <FaHeadset className={'text-gray-300 text-xl w-8'} />
            <div className={'flex flex-col gap-0 leading-none'}>
              <span className={'text-xs mb-1 text-gray-300'}>
                Currently Listening to
              </span>
              <span className={'font-bold text-transparent'}>RVN</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative w-[700px] max-w-[600px] max-h-[80vh] overflow-y-auto bg-blue-900 pt-2 text-white min-h-[100vh]">
        <div className={'flex items-center justify-between gap-4 text-white'}>
          <div className="text-xl pl-2 font-bold min-w-[120px] flex gap-6">
            <span>{utcTime}</span>
            <span>{t.title}</span>
          </div>
          <div className="text-3xl pl-1 min-w-[120px] flex gap-3 text-base font-semibold justify-end items-center">
            <span>{arrivals.length}</span>
            <FaPlaneArrival className={'text-3xl border-[0.5px] p-1 mr-2'} />
          </div>
        </div>

        <div className="overflow-hidden scrollbar-hide">
          <table className="w-full table-auto border-collapse text-sm text-white overflow-hidden scrollbar-hide">
            <thead>
              <tr>
                <th className="text-left pt-2 pl-2 font-medium">{t.time}</th>
                <th className="text-left pt-2 pl-2 font-medium max-w-[160px]">
                  {t.origin}
                </th>
                <th className="text-left pt-2 pl-4 min-w-[100px] font-medium">
                  {t.airline}
                </th>
                <th className="text-left pt-2 pl-4 font-medium">{t.flight}</th>
                <th className="text-left pt-2 pl-4 font-medium">
                  {t.aircraft}
                </th>
                <th className="text-left pl-4 pt-2 w-full font-medium">
                  {t.status}
                </th>
              </tr>
            </thead>

            <tbody>
              {arrivals.map((flight, idx) => {
                const airlineName =
                  flight.airline?.name || flight.airlineIcao || '';
                let logo = flight.airline?.logo || null;

                if (logo && logo.endsWith('/')) {
                  logo = null;
                }

                return (
                  <tr key={idx} className="odd:bg-blue-700 even:bg-blue-900">
                    <td className="py-2 pl-2 pr-1 text-gray-300 font-semibold text-xs">
                      {flight.arrivalTime}
                    </td>

                    <td className="pl-2 text-white font-semibold whitespace-nowrap min-w-[100px] max-w-[160px] overflow-hidden">
                      {flight.origin}
                    </td>

                    <td className={'pl-2'}>
                      {logo ? (
                        <div className="flex items-center justify-center h-full max-w-[80px] pl-2">
                          <img
                            src={logo}
                            alt={''}
                            className="object-contain max-h-[24px] p-0.5 w-full bg-white"
                          />
                        </div>
                      ) : (
                        <p className="text-xs max-w-[80px] ml-1 font-semibold text-gray-200 p-1 whitespace-nowrap text-nowrap overflow-hidden">
                          {airlineName}
                        </p>
                      )}
                    </td>

                    <td className="pl-2 max-w-[80px] font-semibold text-white overflow-hidden whitespace-nowrap text-nowrap">
                      {flight.callsign}
                    </td>

                    <td className="pl-4 pr-4 font-semibold text-white">
                      {flight.flight_plan.aircraft_short}
                    </td>

                    <td className="pl-4 pr-2 text-left font-semibold">
                      <div className="flex items-center gap-1">
                        {/* Status text */}
                        <span className="w-full">
                          {statusTranslations[langs[langIndex]][flight.status]}
                        </span>

                        {flight.status === 'landing' && (
                          <div className="flex gap-0.5">
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-gray-400 animate-ping-bg"
                              style={{
                                animationDelay: `-${animOffsetRef.current}ms`,
                              }}
                            />
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-gray-400 animate-ping-bg"
                              style={{
                                animationDelay: `-${(animOffsetRef.current + HALF_MS) % CYCLE_MS}ms`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FlightBoard;
